/* ==========================================================================
   DOCTORdripp — theme.js
   Vanilla JS only. No jQuery dependency for new code.
   Wires up: header scroll, mobile menu, scroll reveal, product card glow,
   variant pickers, qty steppers, PDP tabs, accord builder card hover,
   parallax garage layers.
   ========================================================================== */

(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Pause perpetual CSS motion when offscreen / tab hidden --- */
  (function initMotionBudget() {
    const syncTabVisibility = () => {
      document.documentElement.classList.toggle('is-tab-hidden', document.hidden);
    };
    syncTabVisibility();
    document.addEventListener('visibilitychange', syncTabVisibility);

    if (reduceMotion || !('IntersectionObserver' in window)) return;

    const motionSelector = [
      '.announcement-bar',
      '.text-rgb',
      '.btn--primary',
      '.btn--gradient',
      '.btn--soldout',
      '.hero-garage__sweep',
      '.hero-garage__underglow',
      '.marquee__track',
      '.bt-page__sweep',
      '.bt-progress__fill',
      '.bt-state__num'
    ].join(',');

    const nodes = document.querySelectorAll(motionSelector);
    if (!nodes.length) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        entry.target.classList.toggle('is-anim-offscreen', !entry.isIntersecting);
      });
    }, { rootMargin: '80px 0px', threshold: 0 });

    nodes.forEach((el) => io.observe(el));
  })();

  /* ---------- Header scrolled state ------------------------------------ */
  const header = document.querySelector('[data-site-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    document.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Mega-menu drawer ----------------------------------------- */
  const menuToggle = document.querySelector('[data-menu-toggle]');
  const drawer     = document.querySelector('[data-mega-drawer]');
  const drawerClose = document.querySelector('[data-mega-drawer-close]');
  function setDrawer(open) {
    if (!drawer) return;
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    document.body.style.overflow = open ? 'hidden' : '';
    if (menuToggle) menuToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }
  menuToggle && menuToggle.addEventListener('click', () => setDrawer(!drawer.classList.contains('is-open')));
  drawerClose && drawerClose.addEventListener('click', () => setDrawer(false));
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') setDrawer(false); });

  /* ---------- Scroll-triggered reveal ---------------------------------- */
  if (!reduceMotion && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    document.querySelectorAll('[data-reveal]').forEach(el => io.observe(el));
  } else {
    document.querySelectorAll('[data-reveal]').forEach(el => el.classList.add('is-visible'));
  }

  /* ---------- Product card cursor-following glow ----------------------- */
  if (!reduceMotion) {
    document.querySelectorAll('[data-product-card]').forEach(card => {
      card.dataset.glowBound = 'true';
      let rect = null;
      const refreshRect = () => { rect = card.getBoundingClientRect(); };
      card.addEventListener('pointerenter', refreshRect);
      window.addEventListener('resize', () => { rect = null; }, { passive: true });
      card.addEventListener('pointermove', (e) => {
        if (!rect) refreshRect();
        const mx = ((e.clientX - rect.left) / rect.width)  * 100;
        const my = ((e.clientY - rect.top)  / rect.height) * 100;
        card.style.setProperty('--mx', mx + '%');
        card.style.setProperty('--my', my + '%');
      });
    });
  }

  /* ---------- PDP variant picker → price/url update -------------------- */
  document.querySelectorAll('[data-product-form]').forEach(wrap => {
    const form = wrap.querySelector('form') || wrap;
    const data = window.__productData && window.__productData[wrap.dataset.productId];
    if (!data) return;
    const radios = form.querySelectorAll('input[type="radio"][name^="options"]');
    const select = form.querySelector('select[name="id"]');
    const idField = form.querySelector('input[name="id"][type="hidden"]');
    // Price block lives in .pdp__price-row, OUTSIDE the form, so scope the
    // lookup to the shared PDP container (fall back to document).
    const scope = wrap.closest('.pdp__details') || wrap.closest('.pdp') || document;
    const priceEl = scope.querySelector('[data-price]');
    const compareEl = scope.querySelector('[data-compare]');
    const badgeEl = scope.querySelector('[data-badge]');
    const priceWrap = scope.querySelector('[data-price-wrap]');
    const buyBtn = form.querySelector('[data-add-button] [data-add-text]');
    // Gallery lives in .pdp__media, a sibling of the form. Reach up to .pdp.
    const pdp = wrap.closest('.pdp') || document;
    const heroImg = pdp.querySelector('[data-pdp-hero-img]');
    const thumbs = pdp.querySelectorAll('[data-pdp-thumb]');

    function baseSrc(u) { return (u || '').split('?')[0]; }

    function swapVariantImage(variant) {
      const img = variant.featured_image;
      if (!heroImg || !img || !img.src) return;
      const targetBase = baseSrc(img.src);
      const joiner = img.src.indexOf('?') > -1 ? '&' : '?';
      const large = img.src + joiner + 'width=1400';
      heroImg.src = large;
      heroImg.srcset = [600, 900, 1200, 1400].map(function (w) {
        return img.src + joiner + 'width=' + w + ' ' + w + 'w';
      }).join(', ');
      if (img.alt) heroImg.alt = img.alt;
      thumbs.forEach(t => {
        t.classList.toggle('is-active', baseSrc(t.dataset.large) === targetBase);
      });
    }

    function pickVariant() {
      const opts = Array.from(form.querySelectorAll('input[type="radio"][name^="options"]:checked')).map(i => i.value);
      const variant = data.variants.find(v => v.options.every((o, i) => o === opts[i]));
      if (!variant) return;
      if (idField) idField.value = variant.id;
      if (select)  select.value  = variant.id;

      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

      if (priceEl) priceEl.textContent = window.formatMoney(variant.price);
      if (compareEl) {
        if (onSale) {
          compareEl.innerHTML = '<s>' + window.formatMoney(variant.compare_at_price) + '</s>';
          compareEl.hidden = false;
        } else {
          compareEl.innerHTML = '<s></s>';
          compareEl.hidden = true;
        }
      }
      if (badgeEl) {
        if (onSale) {
          const pct = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
          badgeEl.textContent = '−' + pct + '%';
          badgeEl.hidden = false;
        } else {
          badgeEl.hidden = true;
        }
      }
      if (priceWrap) priceWrap.classList.toggle('price--on-sale', !!onSale);

      // Availability line: only update when there's no custom build-time value,
      // so per-product overrides (metafield or .items-count CSS) are preserved.
      const availBox = scope.querySelector('[data-availability]');
      if (availBox && availBox.dataset.hasCustom !== 'true') {
        const availVal = availBox.querySelector('[data-availability-value]');
        if (availVal) {
          if (variant.available) {
            availVal.textContent = 'In stock';
            availVal.classList.add('items-count', 'pdp__availability-value--instock');
            availVal.classList.remove('pdp__availability-value--out');
          } else {
            availVal.textContent = 'Sold out';
            availVal.classList.remove('items-count', 'pdp__availability-value--instock');
            availVal.classList.add('pdp__availability-value--out');
          }
        }
      }

      swapVariantImage(variant);

      if (buyBtn) {
        if (variant.available) { buyBtn.textContent = window.theme.strings.addToCart + ' — ' + window.formatMoney(variant.price); }
        else { buyBtn.textContent = window.theme.strings.soldOut; }
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
    radios.forEach(r => r.addEventListener('change', pickVariant));
  });

  /* ---------- Qty stepper --------------------------------------------- */
  document.addEventListener('click', (e) => {
    const stepBtn = e.target.closest('[data-qty-step]');
    if (!stepBtn) return;
    const qty = stepBtn.closest('.qty');
    const input = qty && qty.querySelector('input[type="number"]');
    if (!input) return;
    const dir = parseInt(stepBtn.dataset.qtyStep, 10) || 1;
    const next = Math.max(parseInt(input.min || '1', 10), (parseInt(input.value, 10) || 1) + dir);
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- PDP tabs ------------------------------------------------- */
  document.querySelectorAll('[data-pdp-tabs]').forEach(tabs => {
    const buttons = tabs.querySelectorAll('[data-pdp-tab]');
    const panels  = tabs.querySelectorAll('[data-pdp-panel]');
    buttons.forEach(btn => btn.addEventListener('click', () => {
      buttons.forEach(b => b.classList.remove('is-active'));
      panels.forEach(p => p.classList.remove('is-active'));
      btn.classList.add('is-active');
      const target = tabs.querySelector('[data-pdp-panel="' + btn.dataset.pdpTab + '"]');
      if (target) {
        target.classList.add('is-active');
        // Third-party widgets (e.g. Judge.me reviews) lazy-load when they scroll
        // into view. Inside a display:none tab they never intersect the viewport,
        // so nudge them once the panel is actually visible and measurable.
        requestAnimationFrame(() => {
          try {
            window.dispatchEvent(new Event('resize'));
            window.dispatchEvent(new Event('scroll'));
          } catch (_) {}
        });
      }
    }));
  });

  /* ---------- In-page links → open PDP tab + scroll ------------------- */
  function openPdpTab(tabId) {
    const tabBtn = document.querySelector('[data-pdp-tab="' + tabId + '"]');
    const tabsSection = document.querySelector('[data-pdp-tabs]');
    if (tabBtn) tabBtn.click();
    if (tabsSection) {
      tabsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  document.querySelectorAll('[data-scroll-to-reviews]').forEach(badge => {
    badge.style.cursor = 'pointer';
    badge.addEventListener('click', () => openPdpTab('reviews'));
  });

  document.querySelectorAll('[data-scroll-to-tab]').forEach(link => {
    link.addEventListener('click', e => {
      e.preventDefault();
      const tabId = link.dataset.scrollToTab;
      if (tabId) openPdpTab(tabId);
    });
  });

  document.querySelectorAll('[data-scroll-to-install]').forEach(link => {
    link.addEventListener('click', e => {
      e.preventDefault();
      openPdpTab('install');
    });
  });

  /* ---------- PDP gallery thumbs -------------------------------------- */
  document.querySelectorAll('[data-pdp-gallery]').forEach(gallery => {
    const heroImg = gallery.querySelector('[data-pdp-hero-img]');
    gallery.querySelectorAll('[data-pdp-thumb]').forEach(thumb => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.large || thumb.querySelector('img').src;
        if (heroImg && src) {
          heroImg.src = src;
          if (thumb.dataset.srcset) heroImg.srcset = thumb.dataset.srcset;
          gallery.querySelectorAll('[data-pdp-thumb]').forEach(t => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        }
      });
    });
  });

  /* ---------- Accord builder card subtle parallax tilt ---------------- */
  if (!reduceMotion) {
    document.querySelectorAll('[data-tilt]').forEach(card => {
      let rect = null;
      const refreshRect = () => { rect = card.getBoundingClientRect(); };
      card.addEventListener('pointerenter', refreshRect);
      window.addEventListener('resize', () => { rect = null; }, { passive: true });
      card.addEventListener('pointermove', (e) => {
        if (!rect) refreshRect();
        const mx = (e.clientX - rect.left) / rect.width  - 0.5;
        const my = (e.clientY - rect.top)  / rect.height - 0.5;
        card.style.transform = 'perspective(900px) rotateX(' + (-my * 4) + 'deg) rotateY(' + (mx * 4) + 'deg) translateY(-4px)';
      });
      card.addEventListener('pointerleave', () => { card.style.transform = ''; });
    });
  }

  /* ---------- Hero garage parallax (mouse, fine pointers only) --------- */
  /* Gated to fine pointers so touch swipes don't fire pointermove and shove
     the hero image sideways (touch uses the scroll parallax below instead). */
  if (!reduceMotion && window.matchMedia('(pointer: fine)').matches) {
    const stage = document.querySelector('[data-garage-stage]');
    if (stage) {
      const layers = stage.querySelectorAll('[data-parallax]');
      let stageRect = null;
      const refreshStageRect = () => { stageRect = stage.getBoundingClientRect(); };
      stage.addEventListener('pointerenter', refreshStageRect);
      window.addEventListener('resize', () => { stageRect = null; }, { passive: true });
      stage.addEventListener('pointermove', (e) => {
        if (!stageRect) refreshStageRect();
        const mx = (e.clientX - stageRect.left) / stageRect.width  - 0.5;
        const my = (e.clientY - stageRect.top)  / stageRect.height - 0.5;
        layers.forEach(layer => {
          const depth = parseFloat(layer.dataset.parallax) || 10;
          layer.style.transform = 'translate3d(' + (mx * depth) + 'px, ' + (my * depth * 0.6) + 'px, 0)';
        });
      });
    }
  }

  /* ---------- Hero garage parallax (scroll, touch/mobile) ------------- */
  if (!reduceMotion) {
    const heroPhoto = document.querySelector('.hero-garage__photo');
    const heroStage = document.querySelector('[data-garage-stage]');
    const heroMq = window.matchMedia('(max-width: 749px)');
    if (heroPhoto && heroStage && heroMq.matches) {
      let heroTicking = false;
      let maxShift = heroStage.offsetHeight * 0.16;
      const refreshMaxShift = () => { maxShift = heroStage.offsetHeight * 0.16; };
      const updateHeroParallax = () => {
        /* Drive off absolute scroll so the drift begins from the first pixel
           (the sticky header offsets getBoundingClientRect().top otherwise). */
        const scrolled = window.pageYOffset || document.documentElement.scrollTop || 0;
        const shift = -Math.min(scrolled * 0.3, maxShift);
        heroPhoto.style.transform = 'translate3d(0,' + shift.toFixed(1) + 'px,0)';
        heroTicking = false;
      };
      const onHeroScroll = () => {
        if (!heroTicking) { heroTicking = true; requestAnimationFrame(updateHeroParallax); }
      };
      window.addEventListener('scroll', onHeroScroll, { passive: true });
      window.addEventListener('resize', () => { refreshMaxShift(); updateHeroParallax(); }, { passive: true });
      updateHeroParallax();
    }
  }

  /* ---------- Money formatter (Shopify-compatible) -------------------- */
  window.formatMoney = function (cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, key) {
      switch (key) {
        case 'amount':              return value;
        case 'amount_no_decimals':  return Math.round(cents / 100).toString();
        case 'amount_with_comma_separator': return value.replace('.', ',');
        default: return value;
      }
    });
  };

  /* ---------- Newsletter inline submit ------------------------------- */
  document.querySelectorAll('[data-newsletter-form]').forEach(form => {
    form.addEventListener('submit', () => {
      const btn = form.querySelector('button[type="submit"]');
      if (btn) { btn.disabled = true; btn.textContent = 'Subscribing…'; }
    });
  });

  /* ---------- Collection AJAX filters / sort / pagination ------------ */
  (function initCollectionAjax() {
    const root = document.querySelector('[data-collection-root]');
    if (!root) return;

    const sectionId = root.dataset.sectionId;
    if (!sectionId) return;

    let abortController = null;
    let requestId = 0;

    function observeReveals(scope) {
      const els = scope.querySelectorAll('[data-reveal]:not(.is-visible)');
      if (!els.length) return;
      if (reduceMotion || !('IntersectionObserver' in window)) {
        els.forEach(el => el.classList.add('is-visible'));
        return;
      }
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
      els.forEach(el => io.observe(el));
    }

    function bindCardGlow(scope) {
      if (reduceMotion) return;
      scope.querySelectorAll('[data-product-card]').forEach(card => {
        if (card.dataset.glowBound === 'true') return;
        card.dataset.glowBound = 'true';
        let rect = null;
        const refreshRect = () => { rect = card.getBoundingClientRect(); };
        card.addEventListener('pointerenter', refreshRect);
        window.addEventListener('resize', () => { rect = null; }, { passive: true });
        card.addEventListener('pointermove', (e) => {
          if (!rect) refreshRect();
          const mx = ((e.clientX - rect.left) / rect.width)  * 100;
          const my = ((e.clientY - rect.top)  / rect.height) * 100;
          card.style.setProperty('--mx', mx + '%');
          card.style.setProperty('--my', my + '%');
        });
      });
    }

    function buildFetchUrl(href) {
      const next = new URL(href, window.location.origin);
      const current = new URL(window.location.href);
      // Keep the active sort when switching tag filters.
      if (!next.searchParams.has('sort_by') && current.searchParams.has('sort_by')) {
        next.searchParams.set('sort_by', current.searchParams.get('sort_by'));
      }
      next.searchParams.set('section_id', sectionId);
      return next;
    }

    function publicUrlFromFetch(fetchUrl) {
      const url = new URL(fetchUrl.toString());
      url.searchParams.delete('section_id');
      return url.pathname + url.search;
    }

    async function loadCollection(href, { push = true, scrollMode = 'preserve' } = {}) {
      const results = root.querySelector('[data-collection-results]');
      if (!results) return;

      if (abortController) abortController.abort();
      abortController = new AbortController();
      const thisRequest = ++requestId;

      const fetchUrl = buildFetchUrl(href);
      const scrollY = window.scrollY;

      results.classList.add('is-loading');
      results.setAttribute('aria-busy', 'true');

      try {
        const res = await fetch(fetchUrl.toString(), {
          signal: abortController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!res.ok) throw new Error('Collection fetch failed');
        const html = await res.text();
        if (thisRequest !== requestId) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nextResults = doc.querySelector('[data-collection-results]');
        if (!nextResults) throw new Error('Missing collection results');

        results.innerHTML = nextResults.innerHTML;

        const nextCount = doc.querySelector('[data-collection-count]');
        const countEl = document.querySelector('[data-collection-count]');
        if (nextCount && countEl) countEl.textContent = nextCount.textContent;

        observeReveals(results);
        bindCardGlow(results);

        if (push) {
          window.history.pushState({ collectionAjax: true }, '', publicUrlFromFetch(fetchUrl));
        }

        if (scrollMode === 'toolbar') {
          const toolbar = root.querySelector('#CollectionFilters') || root;
          const top = toolbar.getBoundingClientRect().top + window.scrollY - 90;
          window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' });
        } else {
          window.scrollTo(0, scrollY);
        }
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        // Fall back to a normal navigation if section rendering fails.
        window.location.href = href;
        return;
      } finally {
        if (thisRequest === requestId) {
          results.classList.remove('is-loading');
          results.removeAttribute('aria-busy');
        }
      }
    }

    root.addEventListener('click', (e) => {
      const link = e.target.closest('a[data-collection-filter], .pagination a');
      if (!link || !root.contains(link)) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || link.target === '_blank') return;
      const href = link.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;
      e.preventDefault();
      const scrollMode = link.closest('.pagination') ? 'toolbar' : 'preserve';
      loadCollection(href, { push: true, scrollMode: scrollMode });
    });

    root.addEventListener('change', (e) => {
      const select = e.target.closest('[data-collection-sort]');
      if (!select || !root.contains(select)) return;
      const href = select.value;
      if (!href) return;
      loadCollection(href, { push: true, scrollMode: 'preserve' });
    });

    window.addEventListener('popstate', () => {
      if (!document.querySelector('[data-collection-root]')) return;
      loadCollection(window.location.href, { push: false, scrollMode: 'preserve' });
    });
  })();

})();
